import React, { useState, useEffect } from 'react';
import { tenantAdminQuestionAPI } from '../../../services/api/tenantAdminQuestionAPI';
import useEscKeyHandler from '../../../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  Eye,
  BarChart3,
  Globe,
  Building2,
  Settings,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  Users
} from 'lucide-react';

const PromptManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'global', 'tenant'
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'create', 'edit'
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const promptTypes = [
    { value: 'audio_video', label: 'Audio + Video' },
    { value: 'audio_only', label: 'Audio Only' },
    { value: 'video_only', label: 'Video Only' }
  ];

  useEffect(() => {
    loadQuestions();
    loadAnalytics();
  }, []);

  // Add Esc key handlers for modals
  useEscKeyHandler(showModal, () => setShowModal(false));
  useEscKeyHandler(showAnalytics, () => setShowAnalytics(false));
  useEscKeyHandler(showPreview, () => setShowPreview(false));

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await tenantAdminQuestionAPI.getQuestions();
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Failed to load prompts:', error);
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await tenantAdminQuestionAPI.getQuestionAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load prompt analytics:', error);
    }
  };

  const loadPreviewQuestions = async () => {
    try {
      const response = await tenantAdminQuestionAPI.previewPatientQuestions();
      setPreviewQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Failed to load preview prompts:', error);
      toast.error('Failed to load preview prompts');
    }
  };

  const handleCreatePrompt = () => {
    setSelectedQuestion(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleViewPrompt = (question) => {
    setSelectedQuestion(question);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEditPrompt = (question) => {
    // Only allow editing tenant-specific prompts
    if (question.is_global) {
      toast.error('Cannot edit global prompts. Only organization-specific prompts can be edited.');
      return;
    }
    setSelectedQuestion(question);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeletePrompt = async (questionId, isGlobal) => {
    if (isGlobal) {
      toast.error('Cannot delete global prompts. Only organization-specific prompts can be deleted.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this prompt? This action cannot be undone.')) {
      try {
        await tenantAdminQuestionAPI.deleteQuestion(questionId);
        toast.success('Prompt deleted successfully');
        loadQuestions();
        loadAnalytics();
      } catch (error) {
        toast.error('Failed to delete prompt');
      }
    }
  };

  const handlePreviewPrompts = async () => {
    await loadPreviewQuestions();
    setShowPreview(true);
  };

  const PromptModal = () => {
    const [formData, setFormData] = useState({
      question_text: '',
      instruction_text: '',
      question_type: 'audio_video',
      recording_duration: 30,
      is_active: true
    });

    useEffect(() => {
      if (selectedQuestion && (modalMode === 'edit' || modalMode === 'view')) {
        setFormData({
          question_text: selectedQuestion.question_text || '',
          instruction_text: selectedQuestion.instruction_text || '',
          question_type: selectedQuestion.question_type || 'audio_video',
          recording_duration: selectedQuestion.recording_duration || 30,
          is_active: selectedQuestion.is_active ?? true
        });
      }
    }, [selectedQuestion, modalMode]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (modalMode === 'create') {
          await tenantAdminQuestionAPI.createQuestion(formData);
          toast.success('Organization prompt created successfully');
        } else if (modalMode === 'edit') {
          await tenantAdminQuestionAPI.updateQuestion(selectedQuestion.id, formData);
          toast.success('Prompt updated successfully');
        }
        setShowModal(false);
        loadQuestions();
        loadAnalytics();
      } catch (error) {
        toast.error(`Failed to ${modalMode} prompt`);
      }
    };

    const isReadOnly = modalMode === 'view';
    const isGlobalQuestion = selectedQuestion?.is_global;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-[#F9FBFC] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[#DCEAF5]">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[#0B1C2C]">
                {modalMode === 'create' ? 'Create New Organization Prompt' : 
                 modalMode === 'edit' ? 'Edit Organization Prompt' : 'Prompt Details'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-[#3D3D3D] hover:text-[#207CE5]"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {isGlobalQuestion && modalMode === 'view' && (
              <div className="bg-[#DCEAF5] border border-[#00BCD4] rounded-lg p-4">
                <div className="flex items-center">
                  <Globe className="h-5 w-5 text-[#00BCD4] mr-2" />
                  <span className="text-sm font-medium text-[#0B1C2C]">
                    This is a global prompt managed by system administrators
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                Prompt Text *
              </label>
              <textarea
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                rows={3}
                required
                readOnly={isReadOnly}
                placeholder="Enter the prompt that patients will see..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                Instruction Text
              </label>
              <textarea
                value={formData.instruction_text}
                onChange={(e) => setFormData({ ...formData, instruction_text: e.target.value })}
                className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                rows={2}
                readOnly={isReadOnly}
                placeholder="Optional instructions for patients..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Prompt Type *
                </label>
                <select
                  value={formData.question_type}
                  onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  disabled={isReadOnly}
                  required
                >
                  {promptTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3D3D3D] mb-1">
                  Recording Duration *
                </label>
                <select
                  value={formData.recording_duration}
                  onChange={(e) => setFormData({ ...formData, recording_duration: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                  disabled={isReadOnly}
                  required
                >
                  <option value={15}>15 seconds</option>
                  <option value={30}>30 seconds</option>
                  <option value={60}>60 seconds</option>
                </select>
              </div>
            </div>

            {modalMode === 'edit' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="is_active" className="text-sm text-[#3D3D3D]">
                  Active (visible to patients)
                </label>
              </div>
            )}

            {modalMode === 'view' && selectedQuestion && (
              <div className="bg-[#DCEAF5] p-4 rounded-lg">
                <h4 className="font-medium text-[#0B1C2C] mb-2">Prompt Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[#3D3D3D]">Type:</span>
                    <span className="ml-2 font-medium">
                      {selectedQuestion.is_global ? 'Global Prompt' : 'Organization Prompt'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#3D3D3D]">Status:</span>
                    <span className={`ml-2 font-medium ${selectedQuestion.is_active ? 'text-[#008080]' : 'text-[#00BCD4]'}`}>
                      {selectedQuestion.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#3D3D3D]">Order:</span>
                    <span className="ml-2 font-medium">{selectedQuestion.question_order}</span>
                  </div>
                  <div>
                    <span className="text-[#3D3D3D]">Created:</span>
                    <span className="ml-2 font-medium">
                      {new Date(selectedQuestion.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-6 border-t border-[#DCEAF5]">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-[#3D3D3D] bg-[#F9FBFC] border border-[#DCEAF5] rounded-lg hover:bg-[#DCEAF5]"
              >
                {isReadOnly ? 'Close' : 'Cancel'}
              </button>
              {!isReadOnly && (
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#207CE5] border border-transparent rounded-lg hover:bg-[#4682B4]"
                >
                  {modalMode === 'create' ? 'Create Prompt' : 'Update Prompt'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  };

  const AnalyticsModal = () => {
    if (!analytics) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-[#F9FBFC] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[#DCEAF5]">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[#0B1C2C]">Organization Prompt Analytics</h2>
              <button
                onClick={() => setShowAnalytics(false)}
                className="text-[#3D3D3D] hover:text-[#207CE5]"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                              <div className="bg-[#DCEAF5] p-4 rounded-lg">
                  <div className="flex items-center">
                    <Building2 className="h-8 w-8 text-[#207CE5] mr-3" />
                    <div>
                      <p className="text-sm text-[#207CE5]">Available Prompts</p>
                      <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.total_available_questions}</p>
                    </div>
                  </div>
                </div>

                              <div className="bg-[#DCEAF5] p-4 rounded-lg">
                  <div className="flex items-center">
                    <Globe className="h-8 w-8 text-[#00BCD4] mr-3" />
                    <div>
                      <p className="text-sm text-[#00BCD4]">Global Prompts</p>
                      <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.global_questions_available}</p>
                    </div>
                  </div>
                </div>

                              <div className="bg-[#DCEAF5] p-4 rounded-lg">
                  <div className="flex items-center">
                    <Settings className="h-8 w-8 text-[#008080] mr-3" />
                    <div>
                      <p className="text-sm text-[#008080]">Organization Prompts</p>
                      <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.tenant_specific_questions}</p>
                    </div>
                  </div>
                </div>

              <div className="bg-[#DCEAF5] p-4 rounded-lg">
                <div className="flex items-center">
                  <Activity className="h-8 w-8 text-[#4682B4] mr-3" />
                  <div>
                    <p className="text-sm text-[#4682B4]">Total Responses</p>
                    <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.total_responses}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Prompt Usage</h3>
                <div className="space-y-2">
                  {analytics.question_usage?.slice(0, 10).map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-[#DCEAF5] rounded-lg">
                      <div className="flex items-center flex-1">
                        {item.is_global ? (
                          <Globe className="h-4 w-4 text-[#00BCD4] mr-2 flex-shrink-0" />
                        ) : (
                          <Building2 className="h-4 w-4 text-[#207CE5] mr-2 flex-shrink-0" />
                        )}
                        <div>
                          <p className="text-sm font-medium text-[#0B1C2C]">{item.question_text}</p>
                          <p className="text-xs text-[#3D3D3D]">
                            {item.is_global ? 'Global' : 'Organization'} • {item.response_count} responses
                          </p>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-[#3D3D3D]">
                        {item.response_count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-[#0B1C2C] mb-4">Most Used Prompts</h3>
                <div className="space-y-2">
                  {analytics.most_used_questions?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-[#DCEAF5] rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#0B1C2C]">{item.question_text}</p>
                        <p className="text-xs text-[#3D3D3D]">{item.usage_count} total responses</p>
                      </div>
                      <div className="text-sm font-medium text-[#207CE5]">
                        #{index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PreviewModal = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-[#F9FBFC] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[#DCEAF5]">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#0B1C2C]">Patient Prompt Preview</h2>
                <p className="text-sm text-[#3D3D3D]">Prompts that patients in your organization will see</p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="text-[#3D3D3D] hover:text-[#207CE5]"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {previewQuestions.map((question, index) => (
                <div key={question.id} className="border border-[#DCEAF5] rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="bg-[#DCEAF5] text-[#207CE5] rounded-full w-8 h-8 flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {question.is_global ? (
                            <Globe className="h-4 w-4 text-[#00BCD4]" />
                          ) : (
                            <Building2 className="h-4 w-4 text-[#207CE5]" />
                          )}
                          <span className="text-xs font-medium text-[#3D3D3D]">
                            {question.is_global ? 'Global Prompt' : 'Organization Prompt'}
                          </span>
                          <span className="text-xs text-[#3D3D3D]">•</span>
                          <span className="text-xs text-[#3D3D3D]">
                            {question.question_type?.replace('_', ' ')}
                          </span>
                          <span className="text-xs text-[#3D3D3D]">•</span>
                          <span className="text-xs text-[#3D3D3D]">
                            {question.recording_duration}s
                          </span>
                        </div>
                        <h3 className="text-sm font-medium text-[#0B1C2C] mb-1">
                          {question.question_text}
                        </h3>
                        {question.instruction_text && (
                          <p className="text-xs text-[#3D3D3D]">
                            {question.instruction_text}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {previewQuestions.length === 0 && (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-[#3D3D3D] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0B1C2C] mb-2">No prompts available</h3>
                <p className="text-[#3D3D3D]">No prompts are currently available for patients</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filteredPrompts = questions.filter(question => {
    // Always filter out inactive prompts first
    if (!question.is_active) {
      return false;
    }
    
    const matchesSearch = question.question_text?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'global' && question.is_global) ||
                         (filterType === 'tenant' && !question.is_global);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1C2C]">Prompt Management</h1>
          <p className="text-[#3D3D3D]">Manage prompts for your organization</p>
        </div>
        <div className="flex space-x-3">
          {/* <button
            onClick={handlePreviewQuestions}
            className="flex items-center space-x-2 px-4 py-2 bg-[#008080] text-white rounded-lg hover:bg-[#00BCD4] transition-colors"
          >
            <Play className="h-4 w-4" />
            <span>Preview Patient View</span>
          </button> */}
          {/* <button
            onClick={() => setShowAnalytics(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-[#4682B4] text-white rounded-lg hover:bg-[#207CE5] transition-colors"
          >
            <BarChart3 className="h-4 w-4" />
            <span>Analytics</span>
          </button> */}
          <button
            onClick={handleCreatePrompt}
            className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#4682B4] transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Manage Organization Prompt</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#3D3D3D]" />
            <input
              type="text"
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-[#DCEAF5] rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
          >
            <option value="all">All Prompts</option>
            <option value="global">Global Prompts</option>
            <option value="tenant">Organization Prompts</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4">
                <Building2 className="h-6 w-6 text-[#207CE5]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#3D3D3D]">Available Questions</p>
                <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.total_available_questions}</p>
              </div>
            </div>
          </div> */}

          <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
            <div className="flex items-start">
              <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4 flex-shrink-0">
                <Globe className="h-6 w-6 text-[#00BCD4]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#3D3D3D]">Global Prompts</p>
                <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.global_questions_available}</p>
              </div>
            </div>
          </div>

          <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
            <div className="flex items-start">
              <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4 flex-shrink-0">
                <Settings className="h-6 w-6 text-[#008080]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#3D3D3D]">Organization Prompts</p>
                <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.tenant_specific_questions}</p>
              </div>
            </div>
          </div>

          {/* <div className="bg-[#F9FBFC] p-6 rounded-xl shadow-sm border border-[#DCEAF5]">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-[#DCEAF5] mr-4">
                <Activity className="h-6 w-6 text-[#4682B4]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#3D3D3D]">Total Responses</p>
                <p className="text-2xl font-bold text-[#0B1C2C]">{analytics.total_responses}</p>
              </div>
            </div>
          </div> */}
        </div>
      )}

      {/* Questions List */}
      <div className="bg-[#F9FBFC] rounded-xl shadow-sm border border-[#DCEAF5] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#DCEAF5]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                  Prompt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#3D3D3D] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#F9FBFC] divide-y divide-[#DCEAF5]">
              {filteredPrompts.map((question) => (
                <tr key={question.id} className="hover:bg-[#DCEAF5]">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {question.is_global ? (
                        <Globe className="h-4 w-4 text-[#00BCD4] mr-2 flex-shrink-0" />
                      ) : (
                        <Building2 className="h-4 w-4 text-[#207CE5] mr-2 flex-shrink-0" />
                      )}
                      <div>
                        <div className="text-sm font-medium text-[#0B1C2C]">
                          {question.question_text.length > 60 
                            ? question.question_text.substring(0, 60) + '...'
                            : question.question_text}
                        </div>
                        {question.instruction_text && (
                          <div className="text-sm text-[#3D3D3D]">
                            {question.instruction_text.length > 50
                              ? question.instruction_text.substring(0, 50) + '...'
                              : question.instruction_text}
                          </div>
                        )}
                        <div className="text-xs text-[#3D3D3D] mt-1">
                          {question.is_global ? 'Global Prompt' : 'Organization Prompt'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F9FBFC] text-[#4682B4] border border-[#4682B4]">
                      {question.question_type?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#0B1C2C]">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-[#3D3D3D] mr-1" />
                      {question.recording_duration}s
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {question.is_active ? (
                        <CheckCircle className="h-5 w-5 text-[#008080]" />
                      ) : (
                        <XCircle className="h-5 w-5 text-[#00BCD4]" />
                      )}
                      <span className={`ml-2 text-sm ${
                        question.is_active ? 'text-[#008080]' : 'text-[#00BCD4]'
                      }`}>
                        {question.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#0B1C2C]">
                    #{question.question_order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewPrompt(question)}
                        className="text-[#3D3D3D] hover:text-[#207CE5]"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditPrompt(question)}
                        className={`text-[#3D3D3D] ${
                          question.is_global 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'hover:text-[#207CE5]'
                        }`}
                        title={question.is_global ? 'Cannot edit global prompts' : 'Edit'}
                        disabled={question.is_global}
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePrompt(question.id, question.is_global)}
                        className={`text-[#3D3D3D] ${
                          question.is_global 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'hover:text-[#00BCD4]'
                        }`}
                        title={question.is_global ? 'Cannot delete global prompts' : 'Delete'}
                        disabled={question.is_global}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredPrompts.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-[#3D3D3D] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#0B1C2C] mb-2">No prompts found</h3>
          <p className="text-[#3D3D3D]">
            {searchTerm ? 'Try adjusting your search criteria' : 'Get started by adding your first organization prompt'}
          </p>
        </div>
      )}

      {/* Modals */}
      {showModal && <PromptModal />}
      {showAnalytics && <AnalyticsModal />}
      {showPreview && <PreviewModal />}
    </div>
  );
};

export default PromptManagement;